import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';

const toolName = 'read-page-tree' as const;

const argumentZod = z.object({});

export const readPageTreeTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Read page tree',
  prettyStatus: {
    executing: 'Reading page tree...',
    executed: 'Read page tree',
    failed: 'Failed to read page tree',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Returns the full page element tree — like the Navigator panel. Each line shows the element's readable name and its element ID, indented to reflect nesting. Component instances are marked with \`(component)\` and show their slot structure.

      Always returns the latest state — never cached.

      **Output format:**
      \`\`\`
      Body - abc123
        Navbar - def456 (component)
        Section - ghi789 (component)
          slot (slot) - slot-id-1
            Grid Row - jkl012 (component)
        Section - mno345 (component)
          slot (slot) - pqr678
        Footer - yza567 (component)
      \`\`\`

      **Reading the tree:**
      - Components always show their component name (e.g. "Hero", "Navbar", "Footer")
      - Some components have slots — these appear as indented children with \`(slot)\` marker. Not every component has slots.
      - Slots can contain component instances or plain HTML.
      - Page Slots (labeled "Page Slot") only accept component instances — never plain HTML.
      - Plain elements show as "Div Block", "Section", "Heading 1", etc.
      - If you see "Div Block" where you expected a component, the component render failed — check the component-id and retry

      **Use this tool to:**
      - See the full page structure at a glance
      - Get element IDs for render tool's insertTargetElementId
      - Identify which elements are component instances
      - See slot structure and which component instances are placed in each slot
      - Get slot element IDs for inserting component instances into component slots or Page Slots
      - Verify renders succeeded (components show their name, not "Div Block")

      **When to use read-page-tree vs read-page:**
      - Use read-page-tree for structure and element IDs
      - Use read-page when you need full HTML markup and CSS
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SitePage', action: 'editContent'}],
  confirmBeforeExecuting: false,
  timeout: 30000,
  compaction: {
    prompt:
      'Summarize the page tree structure. Preserve all element names, element IDs, component markers, slot markers, slot content hierarchy, and structural indentation.',
    staleAfterTurns: 3,
    supersededOnRecall: true,
  },
});
